import React, { useState, useMemo, useContext, useEffect } from 'react';
import { AppContext } from '../src/App';
import { useAuth } from './AuthContext';
import { Layout } from './Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Alert, AlertDescription } from './ui/alert';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import {
  Upload,
  Download,
  Search,
  Filter,
  Edit,
  Trash2,
  Eye,
  FileText,
  Users,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Plus,
  Save,
  X,
  Calendar,
  User,
  Clock,
  MoreHorizontal,
  FolderOpen,
  Files
} from 'lucide-react';
import { toast } from 'sonner';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogTitle } from '@radix-ui/react-alert-dialog';
import { AlertDialogFooter, AlertDialogHeader } from './ui/alert-dialog';
import { BreadcrumbHeader } from './BreadCrumbNavigation';


// Mock data for batch files
const mockBatchFiles = [
  {
    id: 'batch-file-001',
    name: 'Q1 2024 Section 28 Audits',
    fileName: 'section_28_q1_2024.csv',
    uploadDate: new Date('2024-01-15'),
    uploadedBy: 'Dr. Sarah Mitchell',
    totalCharts: 45,
    processedCharts: 45,
    status: 'Completed',
    chartType: 'Section 28',
    description: 'First quarter Section 28 audit batch for 2024',
    charts: [
      {
        chartId: 'CHART-001',
        patientId: 'PT-12345',
        auditorDecision: 'Approved',
        auditorReason: 'All documentation complete and accurate',
        auditorName: 'Dr. Sarah Johnson',
        reviewDate: new Date('2024-01-16'),
        chartType: 'Section 28'
      },
      {
        chartId: 'CHART-002',
        patientId: 'PT-12346',
        auditorDecision: 'Send For Correction',
        auditorReason: 'Missing patient signature on consent form',
        auditorName: 'Dr. Sarah Johnson',
        reviewDate: new Date('2024-01-16'),
        chartType: 'Section 28'
      },
      {
        chartId: 'CHART-003',
        patientId: 'PT-12347',
        auditorDecision: 'Send For Correction',
        auditorReason: 'Incomplete medical history documentation',
        auditorName: 'Dr. Michael Chen',
        reviewDate: new Date('2024-01-17'),
        chartType: 'Section 28'
      }
    ]
  },
  {
    id: 'batch-file-002',
    name: 'Emergency Department Reviews',
    fileName: 'ed_reviews_jan_2024.csv',
    uploadDate: new Date('2024-01-20'),
    uploadedBy: 'Dr. Michael Chen',
    totalCharts: 32,
    processedCharts: 28,
    status: 'In Progress',
    chartType: 'Section 17',
    description: 'Emergency department chart reviews for January 2024',
    charts: [
      {
        chartId: 'CHART-004',
        patientId: 'PT-12348',
        auditorDecision: 'Approved',
        auditorReason: 'Complete documentation, meets all standards',
        auditorName: 'Dr. Emily Rodriguez',
        reviewDate: new Date('2024-01-21'),
        chartType: 'Section 17'
      },
      {
        chartId: 'CHART-005',
        patientId: 'PT-12349',
        auditorDecision: 'Approved',
        auditorReason: 'All required fields completed properly',
        auditorName: 'Dr. Emily Rodriguez',
        reviewDate: new Date('2024-01-21'),
        chartType: 'Section 17'
      }
    ]
  },
  {
    id: 'batch-file-003',
    name: 'Outpatient Surgery Reviews',
    fileName: 'outpatient_surgery_feb_2024.csv',
    uploadDate: new Date('2024-02-01'),
    uploadedBy: 'Dr. Emily Rodriguez',
    totalCharts: 28,
    processedCharts: 0,
    status: 'Pending',
    chartType: 'Section 43',
    description: 'Outpatient surgery documentation reviews for February',
    charts: []
  }
];

interface Chart {
  chartId: string;
  patientId: string;
  auditorDecision: string;
  auditorReason: string;
  auditorName: string;
  reviewDate: Date;
  chartType: string;
}

interface BatchFile {
  id: string;
  name: string;
  fileName: string;
  uploadDate: Date;
  uploadedBy: string;
  totalCharts: number;
  processedCharts: number;
  status: string;
  chartType: string;
  description: string;
  charts: Chart[];
}

export function BatchFileManager() {
  const { user, token } = useAuth();
  const { sideBarItems,setCurrentView, setSelectedBatchFileData } = useContext(AppContext);

  const [batchFiles, setBatchFiles] = useState<BatchFile[]>([]);
  const [selectedBatchFile, setSelectedBatchFile] = useState<BatchFile | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingChart, setEditingChart] = useState<Chart | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [batchToDelete, setBatchToDelete] = useState<{ id: string; fileName: string } | null>(null);
  const [nameError, setNameError] = useState('');


  const batchFileMenu = sideBarItems.find((item: any) => item.name === "Batch Files");

  const hasBatchFilePermission = (permission: string) => {
    return batchFileMenu?.userPermissions?.includes(permission);
  };



  // Form states
  const [uploadForm, setUploadForm] = useState({
    name: '',
    description: '',
    file: null as File | null
  });

  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    fileName: ''
  });

  const [chartEditForm, setChartEditForm] = useState({
    auditorDecision: '',
    auditorReason: ''
  });

  const [errors, setErrors] = useState({
    name: "",
    file: "",
  });
  const [touched, setTouched] = useState({
    name: false,
  });

  const validateName = () => {
    if (!uploadForm.name.trim()) {
      setErrors(prev => ({ ...prev, name: "Batch name is required" }));
    } else {
      setErrors(prev => ({ ...prev, name: "" }));
    }
  };

  const validateFile = () => {
    if (!uploadForm.file) {
      setErrors(prev => ({ ...prev, file: "Upload a file" }));
    } else if (uploadForm.file.type !== "application/json") {
      setErrors(prev => ({ ...prev, file: "Only JSON files are allowed" }));
    } else {
      setErrors(prev => ({ ...prev, file: "" }));
    }
  };

  const resetValidation = () => {
    setErrors({
      name: "",
      file: "",
    });
  };

  const validateForm = (): boolean => {
    const nameValid = !!uploadForm.name.trim();
    const fileValid = !!uploadForm.file && uploadForm.file.type === "application/json";

    setErrors({
      name: nameValid ? "" : "Batch name is required",
      file: !uploadForm.file
        ? "Upload a file"
        : uploadForm.file.type !== "application/json"
          ? "Only JSON files are allowed"
          : "",
    });

    return nameValid && fileValid;
  };


  const handleNameBlur = () => {
    if (!editForm.name.trim()) {
      setNameError('Batch name is required.');
    } else {
      setNameError('');
    }
  };




  // Check if user is admin
  // const isAdmin = user?.role === 'Admin';

  const isAdmin = user?.roles?.some(
    (role: any) => role.toLowerCase() === 'admin' || role.toLowerCase() === 'super_admin'
  );




  // Filter batch files
  // const filteredBatchFiles = useMemo(() => {
  //   return batchFiles.filter(batchFile => {
  //     const matchesSearch =
  //       batchFile.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
  //       batchFile.fileName.toLowerCase().includes(searchTerm.toLowerCase()) ||
  //       batchFile.uploadedBy.toLowerCase().includes(searchTerm.toLowerCase()) ||
  //       batchFile.chartType.toLowerCase().includes(searchTerm.toLowerCase());

  //     return matchesSearch;
  //   });
  // }, [batchFiles, searchTerm]);

  // Handle file upload
  // const handleFileUpload = async (e: React.FormEvent) => {
  //   e.preventDefault();
  //   if (!uploadForm.file || !uploadForm.name.trim()) {
  //     toast.error('Please provide a name and select a file');
  //     return;
  //   }

  //   setIsLoading(true);
  //   try {
  //     // Simulate file upload processing
  //     await new Promise(resolve => setTimeout(resolve, 2000));

  //     const newBatchFile: BatchFile = {
  //       id: `batch-file-${Date.now()}`,
  //       name: uploadForm.name,
  //       fileName: uploadForm.file.name,
  //       uploadDate: new Date(),
  //       uploadedBy: user?.name || 'Unknown User',
  //       totalCharts: Math.floor(Math.random() * 50) + 10, // Random for demo
  //       processedCharts: 0,
  //       status: 'Pending',
  //       chartType: 'Section 28', // Default chart type
  //       description: uploadForm.description,
  //       charts: []
  //     };

  //     setBatchFiles(prev => [newBatchFile, ...prev]);
  //     setShowUploadDialog(false);
  //     setUploadForm({ name: '', description: '', file: null });
  //     toast.success('Batch file uploaded successfully');
  //   } catch (error) {
  //     toast.error('Failed to upload batch file');
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  // const handleFileUpload = async (e: React.FormEvent) => {
  //   e.preventDefault();
  //   if (!uploadForm.file || !uploadForm.name.trim()) {
  //     toast.error("Please provide a name and select a file");
  //     return;
  //   }
  //   setIsLoading(true);
  //   try {
  //     // Prepare form data
  //     const formData = new FormData();
  //     formData.append("file", uploadForm.file);
  //     formData.append("batchName", uploadForm.name);
  //     formData.append("description", uploadForm.description || "");
  //     formData.append("uploadedBy", user?.name || "Unknown User");


  //     const response = await fetch(`${import.meta.env.VITE_API_URL}audits/auditor/decision`, {
  //       method: "POST",
  //       headers: {
  //         Authorization: `Bearer ${token}`,
  //       },
  //       body: formData,
  //     });

  //     if (!response.ok) {
  //       throw new Error("Upload failed");
  //     }

  //     const result = await response.json();

  //     // Extract batch file info from response
  //     const apiBatch = result.batchFile.batchFile;
  //     console.log("api", apiBatch);
  //     const newBatchFile: BatchFile = {
  //       id: apiBatch.id,
  //       name: apiBatch.batchName,
  //       fileName: apiBatch.fileName,
  //       uploadDate: apiBatch.createdAt,
  //       uploadedBy: apiBatch.createdBy,
  //       totalCharts: apiBatch.totalCharts,
  //       processedCharts: 0,
  //       status: "Pending",
  //       chartType: apiBatch.chartType,
  //       description: apiBatch.description,
  //       charts: [],
  //     };

  //     setBatchFiles(prev => [newBatchFile, ...prev]);
  //     setShowUploadDialog(false);
  //     setUploadForm({ name: "", description: "", file: null });
  //     fetchBatchFiles();
  //     toast.success(result.message || "Batch file uploaded successfully");
  //   } catch (error) {
  //     console.error(error);
  //     toast.error("Failed to upload batch file");
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  const handleFileUpload = async (e: React.FormEvent) => {
    e.preventDefault();

    const isValid = validateForm(); 

    if (!isValid) {
      toast.error("Please fill the required fields.");
      return;
    }

    setIsLoading(true);

    try {
      const formData = new FormData();
      formData.append("file", uploadForm.file!);
      formData.append("batchName", uploadForm.name);
      formData.append("description", uploadForm.description || "");
      formData.append("uploadedBy", user?.name || "Unknown User");

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}audits/auditor/decision`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || "Upload failed");
      }

      // Success handling...
      const newBatchFile: BatchFile = {
        id: result.data.batchId,
        name: uploadForm.name,
        fileName: uploadForm.file.name,
        uploadDate: uploadForm.createdAt,
        uploadedBy: user?.name || "Unknown User",
        totalCharts: result.data.totalChartDecisions,
        processedCharts: 0,
        status: "Pending",
        chartType: "",
        description: uploadForm.description || "",
        charts: [],
      };

      setBatchFiles(prev => [newBatchFile, ...prev]);
      setShowUploadDialog(false);
      setUploadForm({ name: "", description: "", file: null });
      fetchBatchFiles();
      toast.success(result.message);
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setIsLoading(false);
    }
  };



  // Handle batch file edit
  // const handleEditBatchFile = async (e: React.FormEvent) => {
  //   e.preventDefault();
  //   if (!selectedBatchFile || !editForm.name.trim()) {
  //     toast.error('Please provide a valid name');
  //     return;
  //   }
  //   setIsLoading(true);
  //   try {
  //     await new Promise(resolve => setTimeout(resolve, 1000));

  //     setBatchFiles(prev => prev.map(bf =>
  //       bf.id === selectedBatchFile.id
  //         ? {
  //           ...bf,
  //           name: editForm.name,
  //           description: editForm.description
  //         }
  //         : bf
  //     ));

  //     setShowEditDialog(false);
  //     setSelectedBatchFile(null);
  //     toast.success('Batch file updated successfully');
  //   } catch (error) {
  //     toast.error('Failed to update batch file');
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  const handleEditBatchFile = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedBatchFile) {
      toast.error('Please provide a valid name');
      return;
    }
    setIsLoading(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}audits/batch/${selectedBatchFile.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          batchName: editForm.name,
          description: editForm.description,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update batch file');
      }

      const updatedFile = await response.json();

      setBatchFiles(prev => prev.map(bf =>
        bf.id === selectedBatchFile.id
          ? { ...bf, ...updatedFile.data }
          : bf
      ));

      setShowEditDialog(false);
      setSelectedBatchFile(null);
      fetchBatchFiles();
      toast.success('Batch file updated successfully');
    } catch (error) {
      console.error(error);
      toast.error('Failed to update batch file');
    } finally {
      setIsLoading(false);
    }
  };


  // Handle chart edit
  const handleEditChart = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingChart || !selectedBatchFile) return;

    setIsLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Update the chart in the batch file
      setBatchFiles(prev => prev.map(bf =>
        bf.id === selectedBatchFile.id
          ? {
            ...bf,
            charts: bf.charts.map(chart =>
              chart.chartId === editingChart.chartId
                ? {
                  ...chart,
                  auditorDecision: chartEditForm.auditorDecision,
                  auditorReason: chartEditForm.auditorReason,
                  reviewDate: new Date()
                }
                : chart
            )
          }
          : bf
      ));

      // Update the selected batch file for the view dialog
      if (selectedBatchFile) {
        const updatedBatchFile = {
          ...selectedBatchFile,
          charts: selectedBatchFile.charts.map(chart =>
            chart.chartId === editingChart.chartId
              ? {
                ...chart,
                auditorDecision: chartEditForm.auditorDecision,
                auditorReason: chartEditForm.auditorReason,
                reviewDate: new Date()
              }
              : chart
          )
        };
        setSelectedBatchFile(updatedBatchFile);
      }

      setEditingChart(null);
      toast.success('Chart updated successfully');
    } catch (error) {
      toast.error('Failed to update chart');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle delete batch file
  // const handleDeleteBatchFile = async (batchFileId: string) => {
  //   if (!confirm('Are you sure you want to delete this batch file? This action cannot be undone.')) {
  //     return;
  //   }

  //   setIsLoading(true);
  //   try {
  //     await new Promise(resolve => setTimeout(resolve, 1000));

  //     setBatchFiles(prev => prev.filter(bf => bf.id !== batchFileId));
  //     toast.success('Batch file deleted successfully');
  //   } catch (error) {
  //     toast.error('Failed to delete batch file');
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };
  const handleDeleteBatchFile = async (batchFileId: string) => {
    // if (!confirm('Are you sure you want to delete this batch file? This action cannot be undone.')) {
    //   return;
    // }
    setIsLoading(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}audits/batch/${batchFileId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete batch file');
      }
      setBatchFiles(prev => prev.filter(bf => bf.id !== batchFileId));
      toast.success('Batch file deleted successfully');
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete batch file');
    } finally {
      setIsLoading(false);
    }
  };


  const fetchBatchFiles = async () => {
    try {
      const queryParams = new URLSearchParams();
      if (searchTerm) queryParams.append("search", searchTerm);

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}audits/batch/all?${queryParams.toString()}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch batch files");
      }

      const result = await response.json();
      setBatchFiles(result?.data);
    } catch (error) {
      console.error("Error fetching batch files:", error);
    }
  };


  useEffect(() => {
    fetchBatchFiles();
  }, [searchTerm]);


  // Open edit dialog
  const openEditDialog = (batchFile: BatchFile) => {
    setSelectedBatchFile(batchFile);
    setEditForm({
      name: batchFile.batchName,
      description: batchFile.description,
      fileName: batchFile.fileName
    });
    setShowEditDialog(true);
  };



  // Open batch file detail view
  const openBatchFileDetail = (batchFile: BatchFile) => {
    setSelectedBatchFileData(batchFile);
    setCurrentView('batch-file-detail');
  };

  // Open chart edit
  const openChartEdit = (chart: Chart) => {
    setEditingChart(chart);
    setChartEditForm({
      auditorDecision: chart.auditorDecision,
      auditorReason: chart.auditorReason
    });
  };

  // Get status badge variant
  const getStatusVariant = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed': return 'default';
      case 'in progress': return 'secondary';
      case 'pending': return 'outline';
      default: return 'outline';
    }
  };

  // Get decision badge variant
  const getDecisionVariant = (decision: string) => {
    switch (decision) {
      case 'Approved': return 'default';
      case 'Send For Correction': return 'secondary';
      default: return 'outline';
    }
  };

  // if (!isAdmin) {
  //   return (
  //     <Layout>
  //       <div className="p-6">
  //         <Alert>
  //           <AlertTriangle className="w-4 h-4" />
  //           <AlertDescription>
  //             You don't have permission to access batch file management. This feature is only available to administrators.
  //           </AlertDescription>
  //         </Alert>
  //       </div>
  //     </Layout>
  //   );
  // }

  return (
    <Layout>
      <TooltipProvider>
        <div className="p-6 space-y-6">
          <div>
            <BreadcrumbHeader
                                        // title={`Batch File Management`}  // 👈 Dynamic page title
                                        // paragraph={'Upload, manage, and review batch audit files'}
                                        className='mb-1'
                                        breadcrumbs={[
                                            { label: "Batch Files", onClick: () => setCurrentView('batch-file-manager'),icon:<Files className="w-4 h-4"/> }
                                            
                                        ]}
                                    />
          
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Batch File Management</h1>
              <p className="text-gray-600 mt-1 text-[16px]">
                Upload, manage, and review batch audit files
              </p>
            </div>

            <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
              {/* <DialogTrigger asChild>
                <Button className="flex items-center gap-2">
                  <Upload className="w-4 h-4" />
                  Upload Batch File
                </Button>
              </DialogTrigger> */}
              {hasBatchFilePermission("chart:create") && (
              <DialogTrigger asChild>
                <Button
                  className="flex items-center gap-2"
                  onClick={() => {
                    setUploadForm({ name: "", description: "", file: null });
                    resetValidation();
                  }}
                >
                  <Upload className="w-4 h-4" />
                  Upload Batch File
                </Button>
              </DialogTrigger>)}
              <DialogContent className="sm:max-w-md">
                <form onSubmit={handleFileUpload}>
                  <DialogHeader>
                    <DialogTitle>Upload New Batch File</DialogTitle>
                    <DialogDescription>
                      Upload a Batch file containing chart audit data
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    {/* <div className="space-y-2">
                      <label className="text-sm font-medium">Batch Name *</label>
                      <Input
                        value={uploadForm.name}
                        onChange={(e) => setUploadForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Enter batch name"
                        required
                      />
                    </div> */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Batch Name *</label>
                      <Input
                        value={uploadForm.name}
                        onChange={(e) => {
                          setUploadForm((prev) => ({ ...prev, name: e.target.value }));
                          if (touched.name) {
                            setErrors((prev) => ({ ...prev, name: "" }));
                          }
                        }}
                        onBlur={() => {
                          setTouched((prev) => ({ ...prev, name: true }));
                          if (!uploadForm.name.trim()) {
                            setErrors((prev) => ({ ...prev, name: "Batch name is required" }));
                          } else {
                            setErrors((prev) => ({ ...prev, name: "" }));
                          }
                        }}
                        placeholder="Enter batch name"
                      />
                      {touched.name && errors.name && (
                        <p className="text-sm text-red-500">{errors.name}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Description</label>
                      <Textarea
                        value={uploadForm.description}
                        onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Optional description"
                        rows={3}
                      />
                    </div>
                    {/* <div className="space-y-2">
                      <label className="text-sm font-medium">JSON File *</label>
                      <Input
                        type="file"
                        accept=".json"
                        onChange={(e) => setUploadForm(prev => ({ ...prev, file: e.target.files?.[0] || null }))}
                        required
                      />
                      <p className="text-xs text-gray-500">
                        Supported format: JSON files only
                      </p>
                    </div> */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Batch File *</label>

                      <input
                        id="json-file-upload"
                        type="file"
                        accept=".json"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0] || null;
                          setUploadForm((prev) => ({ ...prev, file }));
                          if (!file) {
                            setErrors((prev) => ({ ...prev, file: "Upload a file" }));
                          } else if (file.type !== "application/json") {
                            setErrors((prev) => ({ ...prev, file: "Only JSON files are allowed" }));
                          } else {
                            setErrors((prev) => ({ ...prev, file: "" }));
                          }
                        }}
                        onBlur={() => {
                          if (!uploadForm.file) {
                            setErrors((prev) => ({ ...prev, file: "Upload a file" }));
                          } else if (uploadForm.file.type !== "application/json") {
                            setErrors((prev) => ({ ...prev, file: "Only JSON files are allowed" }));
                          } else {
                            setErrors((prev) => ({ ...prev, file: "" }));
                          }
                        }}
                      />

                      <div className="flex items-center space-x-2">
                        <button
                          type="button"
                          disabled={!!uploadForm.file}
                          className={`px-3 py-0.5 text-sm rounded border border-black 
        ${uploadForm.file
                              ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                              : "bg-gray-50 text-black hover:bg-gray-200"}`}
                          onClick={() =>
                            document.getElementById("json-file-upload")?.click()
                          }
                        >
                          Choose File
                        </button>

                        {uploadForm.file && (
                          <>
                            <span className="text-sm text-gray-700">{uploadForm.file.name}</span>
                            <button
                              type="button"
                              className="px-3 py-0.5 text-sm bg-gray-50 text-red-500 rounded border border-black hover:bg-red-400 hover:text-white"
                              onClick={() => {
                                setUploadForm((prev) => ({ ...prev, file: null }));
                                setErrors((prev) => ({ ...prev, file: "Upload a file" }));
                                const input = document.getElementById("json-file-upload") as HTMLInputElement;
                                if (input) input.value = "";
                              }}
                            >
                              Remove
                            </button>
                          </>
                        )}
                      </div>

                      {errors.file && <p className="text-sm text-red-500">{errors.file}</p>}
                      <p className="text-xs text-gray-500">Allowed format: JSON</p>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setShowUploadDialog(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={isLoading}>
                      {isLoading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Upload
                        </>
                      )}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          </div>

          {/* Filters */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <Input
                      placeholder="Search by name, file name, chart type, or uploader..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Batch Files Table */}
          <Card>
            <CardHeader>
              <CardTitle className='text-[16px]'>Batch Files</CardTitle>
              <CardDescription className='text-[14px]'>
                Showing {batchFiles.length} batch files • Click any row to view batch details
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195]">Name</TableHead>
                      <TableHead className="font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195]">File Name</TableHead>
                      <TableHead className="font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195]"># of Charts</TableHead>
                      <TableHead className="font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195]">Chart Type</TableHead>
                      <TableHead className="font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195]">Uploaded By</TableHead>
                      <TableHead className="font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195]">Upload Date</TableHead>
                      <TableHead className="w-12 font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {batchFiles.map((batchFile) => (
                      <TableRow
                        key={batchFile?.id}
                        className="cursor-pointer hover:bg-gray-50 transition-colors"
                        onClick={() => openBatchFileDetail(batchFile)}
                      >
                        <TableCell>
                          <div>
                            <div className="font-medium">{batchFile?.batchName}</div>
                            {batchFile?.description && (
                              <div className="text-sm text-gray-500 mt-1">
                                {batchFile?.description}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="font-medium text-sm">{batchFile?.fileName}</TableCell>
                        <TableCell>
                          <span className="font-medium">{batchFile?.totalCharts}</span>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline"  className="font-medium text-sm">
                            {batchFile?.chartType}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-gray-400" />
                            <span className="text-sm">{batchFile?.createdByUser?.firstName}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            <span className="text-sm">
                              {batchFile?.createdAt ? new Date(batchFile.createdAt).toLocaleDateString() : ''}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-1">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => openBatchFileDetail(batchFile)}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>View Charts</TooltipContent>
                            </Tooltip>

                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                onClick={() => {
                                  if (hasBatchFilePermission("chart:create")) {
                                    openEditDialog(batchFile);
                                   } else {
                                    toast.error("You need 'chart:create' permission to edit batch files or contact for administrator.");
                                   }}}>
                                  <Edit className="w-4 h-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Edit Batch</TooltipContent>
                            </Tooltip>

                            <Tooltip>
                              <TooltipTrigger asChild>
                                {/* <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => { handleDeleteBatchFile(batchFile.id) }}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button> */}
                                {hasBatchFilePermission("chart:create") && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    setBatchToDelete(batchFile);
                                    setShowDeleteDialog(true);
                                  }}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>)}
                              </TooltipTrigger>
                              <TooltipContent>Delete Batch</TooltipContent>
                            </Tooltip>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {batchFiles.length === 0 && (
                  <div className="text-center py-8">
                    <FolderOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">
                      {searchTerm
                        ? 'No batch files match your search'
                        : 'No batch files uploaded yet'
                      }
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Edit Batch Dialog */}
          <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
            <DialogContent className="sm:max-w-md">
              <form onSubmit={handleEditBatchFile}>
                <DialogHeader>
                  <DialogTitle>Edit Batch File</DialogTitle>
                  <DialogDescription>
                    Update batch file information
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  {/* <div className="space-y-2">
                    <label className="text-sm font-medium">Batch Name *</label>
                    <Input
                      value={editForm.name}
                      onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Enter batch name"
                      required
                    />
                  </div> */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Batch Name *</label>
                    <Input
                      value={editForm.name}
                      onChange={(e) => {
                        setEditForm((prev) => ({ ...prev, name: e.target.value }));
                        if (nameError) setNameError(''); // Clear error on change
                      }}
                      onBlur={handleNameBlur}
                      placeholder="Enter batch name"
                      required
                      className={nameError ? 'border-red-500 focus-visible:ring-red-500' : ''}
                    />
                    {nameError && <p className="text-sm text-red-500">{nameError}</p>}
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Description</label>
                    <Textarea
                      value={editForm.description}
                      onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Optional description"
                      rows={3}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isLoading}>
                    {isLoading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* Delete Betch Dialog */}
          <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Delete Batch File</DialogTitle>
                <DialogDescription>
                  <DialogDescription>
                    Are you sure you want to delete ' {batchToDelete?.fileName} ' ? This action cannot be undone.
                  </DialogDescription>
                </DialogDescription>
              </DialogHeader>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowDeleteDialog(false)}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={async () => {
                    if (batchToDelete) {
                      await handleDeleteBatchFile(batchToDelete.id);
                      setShowDeleteDialog(false);
                    }
                  }}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Confirm Delete
                    </>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>




          {/* Edit Chart Dialog */}
          <Dialog open={!!editingChart} onOpenChange={() => setEditingChart(null)}>
            <DialogContent className="sm:max-w-md">
              <form onSubmit={handleEditChart}>
                <DialogHeader>
                  <DialogTitle>Edit Chart Decision</DialogTitle>
                  <DialogDescription>
                    Update auditor decision for Chart {editingChart?.chartId}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Auditor Decision *</label>
                    <Select
                      value={chartEditForm.auditorDecision}
                      onValueChange={(value) => setChartEditForm(prev => ({ ...prev, auditorDecision: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select decision" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Approved">Approved</SelectItem>
                        <SelectItem value="Send For Correction">Send For Correction</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">
                      Reason {chartEditForm.auditorDecision === 'Send For Correction' && '*'}
                    </label>
                    <Textarea
                      value={chartEditForm.auditorReason}
                      onChange={(e) => setChartEditForm(prev => ({ ...prev, auditorReason: e.target.value }))}
                      placeholder={
                        chartEditForm.auditorDecision === 'Send For Correction'
                          ? "Explain what needs to be corrected..."
                          : "Optional reason for approval..."
                      }
                      required={chartEditForm.auditorDecision === 'Send For Correction'}
                      rows={3}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setEditingChart(null)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isLoading}>
                    {isLoading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </TooltipProvider>
    </Layout>
  );
}